import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface RouteCoordinate {
  lat: number;
  lng: number;
}

interface RunningRouteVectorProps {
  coordinates: RouteCoordinate[];
  width?: number;
  height?: number;
  strokeColor?: string;
  strokeWidth?: number;
  showStartEnd?: boolean;
  className?: string;
}

/**
 * Renders GPS coordinates as an SVG path for share templates.
 * Automatically scales and centers the route within the given dimensions.
 */
export const RunningRouteVector = ({
  coordinates,
  width = 400,
  height = 400,
  strokeColor = '#0AFFC2',
  strokeWidth = 3,
  showStartEnd = false,
  className = '',
}: RunningRouteVectorProps) => {
  const { t } = useTranslation();
  const { pathData, startPoint, endPoint, svgPoints } = useMemo(() => {
    if (!coordinates || coordinates.length < 2) {
      return { pathData: '', startPoint: null, endPoint: null, svgPoints: [] as {x:number;y:number}[] };
    }

    // Find bounds
    const lats = coordinates.map(c => c.lat);
    const lngs = coordinates.map(c => c.lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    // Add padding (20% on each side to leave room for labels)
    const padding = 0.2;
    const latRange = maxLat - minLat || 0.001;
    const lngRange = maxLng - minLng || 0.001;
    const paddedLatRange = latRange * (1 + padding * 2);
    const paddedLngRange = lngRange * (1 + padding * 2);
    const paddedMinLat = minLat - latRange * padding;
    const paddedMinLng = minLng - lngRange * padding;

    const scaleX = width / paddedLngRange;
    const scaleY = height / paddedLatRange;
    const scale = Math.min(scaleX, scaleY);

    const offsetX = (width - paddedLngRange * scale) / 2;
    const offsetY = (height - paddedLatRange * scale) / 2;

    const toSvgPoint = (coord: RouteCoordinate) => ({
      x: (coord.lng - paddedMinLng) * scale + offsetX,
      y: height - ((coord.lat - paddedMinLat) * scale + offsetY),
    });

    const svgPoints = coordinates.map(toSvgPoint);
    
    const pathCommands = svgPoints.map((point, i) => 
      `${i === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`
    );

    return {
      pathData: pathCommands.join(' '),
      startPoint: svgPoints[0],
      endPoint: svgPoints[svgPoints.length - 1],
      svgPoints,
    };
  }, [coordinates, width, height]);

  if (!pathData) {
    return null;
  }

  return (
    <svg 
      width={width} 
      height={height} 
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      style={{ overflow: 'visible' }}
    >
      {/* Route path with glow effect */}
      <defs>
        <filter id="routeGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>

      {/* Glow layer */}
      <path
        d={pathData}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth * 2}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.3}
        filter="url(#routeGlow)"
      />

      {/* Main route line */}
      <path
        d={pathData}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Start/End markers */}
      {showStartEnd && startPoint && endPoint && svgPoints.length >= 2 && (() => {
        // Helper: find minimum distance from a point to any route point
        const minDistToRoute = (px: number, py: number) => {
          let minDist = Infinity;
          const step = Math.max(1, Math.floor(svgPoints.length / 50));
          for (let i = 0; i < svgPoints.length; i += step) {
            const dx = px - svgPoints[i].x;
            const dy = py - svgPoints[i].y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < minDist) minDist = dist;
          }
          return minDist;
        };

        // Check min distance from a text bounding box to route
        // Samples corners + edges of the text rect
        const minDistTextBoxToRoute = (cx: number, cy: number, tw: number, th: number) => {
          const left = cx - tw / 2;
          const right = cx + tw / 2;
          const top = cy - th;
          const bottom = cy;
          // Sample 8 points around the text box
          const testPoints = [
            { x: left, y: top }, { x: cx, y: top }, { x: right, y: top },
            { x: left, y: (top + bottom) / 2 }, { x: right, y: (top + bottom) / 2 },
            { x: left, y: bottom }, { x: cx, y: bottom }, { x: right, y: bottom },
          ];
          let minDist = Infinity;
          for (const p of testPoints) {
            const d = minDistToRoute(p.x, p.y);
            if (d < minDist) minDist = d;
          }
          return minDist;
        };

        // Find best START label position
        const findStartLabelPos = (anchorX: number, anchorY: number) => {
          const textW = 85; // approximate "START" text width
          const textH = 22;
          const angles = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];
          const distances = [25, 35, 48, 62];
          let bestX = anchorX;
          let bestY = anchorY + 30;
          let bestScore = -Infinity;

          for (const dist of distances) {
            for (const angleDeg of angles) {
              const rad = (angleDeg * Math.PI) / 180;
              const cx = anchorX + Math.cos(rad) * dist;
              const cy = anchorY + Math.sin(rad) * dist;

              // Bounds check
              if (cx - textW / 2 < 5 || cx + textW / 2 > width - 5) continue;
              if (cy - textH < 5 || cy > height - 5) continue;

              // Check clearance using the FULL text bounding box
              const boxClearance = minDistTextBoxToRoute(cx, cy, textW, textH);
              if (boxClearance < 12) continue; // must be >12px clear of route

              // Score: prioritize proximity to anchor, with mild clearance bonus
              const score = -dist * 1.5 + Math.min(boxClearance, 30) * 0.3;

              if (score > bestScore) {
                bestScore = score;
                bestX = cx;
                bestY = cy;
              }
            }
          }
          return { x: bestX, y: bestY };
        };

        const startLabelPos = findStartLabelPos(startPoint.x, startPoint.y);

        // Find best flag position (reuse simple approach - flag is small)
        const findFlagPos = (anchorX: number, anchorY: number) => {
          const angles = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];
          const distances = [25, 35, 48];
          let bestX = anchorX;
          let bestY = anchorY - 30;
          let bestScore = -Infinity;
          for (const dist of distances) {
            for (const angleDeg of angles) {
              const rad = (angleDeg * Math.PI) / 180;
              const cx = anchorX + Math.cos(rad) * dist;
              const cy = anchorY + Math.sin(rad) * dist;
              if (cx < 5 || cx + 25 > width - 5) continue;
              if (cy - 40 < 5 || cy > height - 5) continue;
              const routeDist = minDistToRoute(cx, cy - 20);
              if (routeDist < 10) continue;
              const score = -dist * 1.5 + Math.min(routeDist, 30) * 0.3;
              if (score > bestScore) { bestScore = score; bestX = cx; bestY = cy; }
            }
          }
          return { x: bestX, y: bestY };
        };
        const flagPos = findFlagPos(endPoint.x, endPoint.y);

        return (
          <>
            <circle
              cx={startPoint.x}
              cy={startPoint.y}
              r={strokeWidth * 1.2}
              fill={strokeColor}
            />
            <text
              x={startLabelPos.x}
              y={startLabelPos.y}
              textAnchor="middle"
              fill={strokeColor}
              fontSize="22"
              fontWeight="700"
              fontFamily="sans-serif"
              letterSpacing="2"
            >
              {t('components.shareTemplatesExtra.start')}
            </text>

            <g transform={`translate(${flagPos.x - 11}, ${flagPos.y - 20})`}>
              <rect x="0" y="0" width="3" height="40" fill={strokeColor} rx="1" />
              <path d="M3 0 L22 5 L3 13 Z" fill={strokeColor} />
            </g>
          </>
        );
      })()}
    </svg>
  );
};

export default RunningRouteVector;
